import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { seasonOf } from '@il-colpo/meta/progress';
import { formatTag } from '@il-colpo/shared/account';
import { AvatarFigure } from '../../account/AvatarFigure.tsx';
import { useMetaClient, useRemote } from '../../meta/hooks.ts';
import type { BoardKind, BoardRow } from '../../meta/types.ts';
import { Panel, ScreenFrame, Tabs } from '../../ui/index.ts';
import { Failed, GatePanel, Page, useMetaGate } from './common.tsx';
import styles from './Meta.module.css';

const BOARDS: readonly BoardKind[] = ['thief', 'infiltrator', 'xp'];

const TAB: Record<
  BoardKind,
  'leaderboards.tabThief' | 'leaderboards.tabInfiltrator' | 'leaderboards.tabXp'
> = {
  thief: 'leaderboards.tabThief',
  infiltrator: 'leaderboards.tabInfiltrator',
  xp: 'leaderboards.tabXp',
};

function Board({ kind, seasonId }: { kind: BoardKind; seasonId: string }) {
  const { t } = useTranslation('meta');
  const meta = useMetaClient();
  const load = useMemo(
    () => (meta ? () => meta.leaderboard(kind, seasonId, 50) : null),
    [meta, kind, seasonId],
  );
  const board = useRemote<BoardRow[]>(load);

  if (board.status === 'error') return <Failed onRetry={board.reload} />;
  if (board.status === 'loading') {
    return (
      <p className={styles['note']} role="status">
        {t('gate.loading')}
      </p>
    );
  }
  const rows = board.data ?? [];
  if (rows.length === 0) return <p className={styles['note']}>{t('leaderboards.empty')}</p>;
  const me = rows.some((r) => r.isMe);
  return (
    <div className={styles['stack']}>
      <ol className={styles['board']}>
        {rows.map((r) => (
          <li
            key={`${String(r.position)}-${r.nickname}-${String(r.tag)}`}
            className={styles['boardRow']}
            data-me={r.isMe}
          >
            <span className={styles['position']}>{r.position}</span>
            <AvatarFigure avatar={r.avatar} size="2.6rem" />
            <span className={styles['who']}>
              <strong>{`${r.nickname}#${formatTag(r.tag)}`}</strong>
              <span className={styles['note']}>
                {kind === 'xp'
                  ? t('leaderboards.level', { level: r.detail })
                  : t('leaderboards.games', { games: r.detail })}
                {r.isMe ? ` · ${t('leaderboards.you')}` : ''}
              </span>
            </span>
            <span className={styles['score']}>{r.value}</span>
          </li>
        ))}
      </ol>
      {me ? null : <p className={styles['note']}>{t('leaderboards.youOutside')}</p>}
    </div>
  );
}

/** `/leaderboards`: the season's two ratings and the experience, with the player's own row marked. */
export function LeaderboardScreen() {
  const { t } = useTranslation('meta');
  const gate = useMetaGate();
  const [kind, setKind] = useState<BoardKind>('thief');
  const [now] = useState(() => Date.now());
  const season = seasonOf(now);
  return (
    <ScreenFrame title={t('leaderboards.title')} backTo="/">
      <Page>
        {gate !== 'ready' ? <GatePanel gate={gate} /> : null}
        {gate === 'ready' ? (
          <Panel>
            <div className={styles['stack']}>
              {kind === 'xp' ? null : (
                <p className={styles['note']}>
                  <strong>{t('leaderboards.season', { n: season.index })}</strong>{' '}
                  {t('leaderboards.seasonHint')}
                </p>
              )}
              <Tabs
                label={t('leaderboards.tabs')}
                active={kind}
                onChange={setKind}
                tabs={BOARDS.map((b) => ({ key: b, label: t(TAB[b]) }))}
              >
                <h2 className={styles['subtitle']}>{t(`leaderboards.${kind}`)}</h2>
                <Board key={kind} kind={kind} seasonId={season.id} />
              </Tabs>
            </div>
          </Panel>
        ) : null}
      </Page>
    </ScreenFrame>
  );
}
